import { AccountE2eEncryption } from '@api/types';
import { RavIcon, RavIconCircled, RavIconCircledOutlined, RavIconOutlined } from '@shared/icon/icon';
import { Scope } from '@shared/types/scope';

export enum ActionEnabledRule {
  Always,
  OnOneSelectionOnly,
  OnOneOrMoreSelections,
  OnTwoOrMoreSelections,
  OnDocumentOnlySelections,
  OnTwoPdfsSelected,
  E2EEncryptionShouldBeDisabled,
  If2OrMoreSelected_E2EEShouldBeDisabled,
  OnNoErrorFilesSelected
}

export class ActionTrigger<T> {
  data: T[];
  name: string;
  metadata: any;

  constructor(name: string, data: any[], metadata?: any) {
    this.name = name;
    this.data = data;
    this.metadata = metadata;
  }
}

export interface TableAction {
  name: string;
  shortName?: string;
  iconEnabled: RavIconCircled;
  iconDisabled?: RavIconCircledOutlined;
  iconRightClickMenu?: string;
  mobileIcon: RavIconOutlined | RavIcon;
  enabledRules: ActionEnabledRule[];
  showingRules?: ActionEnabledRule[];
  scope?: '*' | Scope[];
  disabled?: boolean;
  tooltip?: string;
}

export interface RowAction {
  name: string;
  cellValueOverride?: string;
  scope?: string;
}

export class TableActions {
  static addContact: TableAction = {
    name: 'Add New',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.AddUser,
    enabledRules: [ActionEnabledRule.Always],
    scope: '*'
  };

  static editContact: TableAction = {
    name: 'Edit',
    iconEnabled: RavIconCircled.Pencil,
    iconDisabled: RavIconCircledOutlined.Pencil,
    mobileIcon: RavIcon.Pencil,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly],
    scope: '*'
  };

  static deleteContact: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: '*'
  };

  static importContacts: TableAction = {
    name: 'Import',
    iconEnabled: RavIconCircled.ImportCsv,
    mobileIcon: RavIcon.ImportCsv,
    enabledRules: [ActionEnabledRule.Always],
    scope: '*'
  };

  static combineDocument: TableAction = {
    name: 'Combine',
    iconEnabled: RavIconCircled.Combine,
    iconDisabled: RavIconCircledOutlined.Combine,
    mobileIcon: RavIconOutlined.Combine,
    enabledRules: [ActionEnabledRule.OnTwoPdfsSelected],
    showingRules: [ActionEnabledRule.E2EEncryptionShouldBeDisabled],
    scope: [Scope.DocumentWrite]
  };

  static downloadPublicDocument: TableAction = {
    name: 'Download',
    iconEnabled: RavIconCircled.Download,
    mobileIcon: RavIconOutlined.Download,
    enabledRules: [ActionEnabledRule.Always],
    scope: '*'
  };

  static shareDocument: TableAction = {
    name: 'Share',
    iconEnabled: RavIconCircled.ArrowRight,
    mobileIcon: RavIconOutlined.Share,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentWrite, Scope.FolderWrite]
  };

  static editDocument: TableAction = {
    name: 'Editor',
    iconEnabled: RavIconCircled.Note,
    iconDisabled: RavIconCircledOutlined.Note,
    mobileIcon: RavIconOutlined.Note,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentWrite]
  };

  static downloadDocument: TableAction = {
    name: 'Download',
    iconEnabled: RavIconCircled.Download,
    iconDisabled: RavIconCircledOutlined.Download,
    mobileIcon: RavIconOutlined.Download,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentFile]
  };

  static archiveDocument: TableAction = {
    name: 'Archive',
    iconEnabled: RavIconCircled.Box,
    iconDisabled: RavIconCircledOutlined.Box,
    mobileIcon: RavIconOutlined.Archive,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentDelete]
  };

  static deleteDocument: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentDelete]
  };

  static moveDocument: TableAction = {
    name: 'Move',
    iconEnabled: RavIconCircled.Move,
    iconDisabled: RavIconCircledOutlined.Move,
    mobileIcon: RavIconOutlined.Move,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.DocumentWrite]
  };

  static archiveFilesystem: TableAction = {
    name: 'Archive',
    iconEnabled: RavIconCircled.Box,
    iconDisabled: RavIconCircledOutlined.Box,
    mobileIcon: RavIconOutlined.Archive,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections, ActionEnabledRule.OnNoErrorFilesSelected],
    scope: [Scope.DocumentDelete]
  };

  static combineFilesystem: TableAction = {
    name: 'Combine',
    iconEnabled: RavIconCircled.Combine,
    iconDisabled: RavIconCircledOutlined.Combine,
    mobileIcon: RavIconOutlined.Combine,
    enabledRules: [ActionEnabledRule.OnTwoPdfsSelected, ActionEnabledRule.OnNoErrorFilesSelected],
    showingRules: [ActionEnabledRule.E2EEncryptionShouldBeDisabled],
    scope: [Scope.DocumentWrite]
  };

  static deleteFilesystem: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.DocumentDelete]
  };
  static restoreFilesystem: TableAction = {
    name: 'Restore',
    iconEnabled: RavIconCircled.BackwardsArrow,
    iconDisabled: RavIconCircledOutlined.BackwardsArrow,
    mobileIcon: RavIcon.BackwardsArrow,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.DocumentWrite, Scope.FolderWrite]
  };

  static moveFilesystem: TableAction = {
    name: 'Move',
    iconEnabled: RavIconCircled.Move,
    iconDisabled: RavIconCircledOutlined.Move,
    mobileIcon: RavIconOutlined.Move,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections, ActionEnabledRule.OnNoErrorFilesSelected],
    scope: [Scope.DocumentWrite]
  };

  static shareFilesystem: TableAction = {
    name: 'Share',
    iconEnabled: RavIconCircled.ArrowRight,
    iconDisabled: RavIconCircledOutlined.ArrowRight,
    mobileIcon: RavIconOutlined.Share,
    enabledRules: [ActionEnabledRule.OnDocumentOnlySelections, ActionEnabledRule.OnNoErrorFilesSelected],
    scope: '*'
  };

  static downloadFileSystem: TableAction = {
    name: 'Download',
    iconEnabled: RavIconCircled.Download,
    iconDisabled: RavIconCircledOutlined.Download,
    mobileIcon: RavIconOutlined.Download,
    enabledRules: [
      ActionEnabledRule.OnOneOrMoreSelections,
      ActionEnabledRule.OnNoErrorFilesSelected,
      ActionEnabledRule.If2OrMoreSelected_E2EEShouldBeDisabled
    ],
    scope: [Scope.DocumentFile]
  };

  static createNewUser: TableAction = {
    name: 'Create New',
    shortName: 'New',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.AddUser,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.UserWrite]
  };

  static deleteUser: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.UserDelete]
  };

  static editUser: TableAction = {
    name: 'Edit',
    iconEnabled: RavIconCircled.Pencil,
    iconDisabled: RavIconCircledOutlined.Pencil,
    mobileIcon: RavIcon.Pencil,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly],
    scope: [Scope.UserWrite]
  };

  static createNewTemplate: TableAction = {
    name: 'Create New',
    shortName: 'New',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.Add,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.TemplateWrite]
  };

  static deleteTemplate: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.TemplateDelete]
  };

  static createNewAccount: TableAction = {
    name: 'Create New',
    shortName: 'New',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.Add,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.AccountWrite]
  };

  static deleteAccount: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.AccountWrite]
  };

  static createNew: TableAction = {
    name: 'Create New',
    shortName: 'New',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.Add,
    enabledRules: [ActionEnabledRule.Always]
  };

  static createNewFolder: TableAction = {
    name: 'Create New',
    shortName: 'New',
    iconEnabled: RavIconCircled.Folder,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.AddFolder,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.FolderWrite]
  };

  static deleteFolder: TableAction = {
    name: 'Delete',
    iconEnabled: RavIconCircled.Trash,
    iconDisabled: RavIconCircledOutlined.Trash,
    mobileIcon: RavIconOutlined.Trash,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.FolderDelete]
  };

  static archiveFolder: TableAction = {
    name: 'Archive',
    iconEnabled: RavIconCircled.Box,
    iconDisabled: RavIconCircledOutlined.Box,
    mobileIcon: RavIconOutlined.Archive,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: [Scope.FolderDelete]
  };

  static notes: TableAction = {
    name: 'Notes',
    iconEnabled: RavIconCircled.Note,
    iconDisabled: RavIconCircledOutlined.Note,
    mobileIcon: RavIconOutlined.Note,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.AccountWrite]
  };

  static addNotes: TableAction = {
    name: 'Add Note',
    iconEnabled: RavIconCircled.Add,
    iconDisabled: RavIconCircledOutlined.Add,
    mobileIcon: RavIconOutlined.Add,
    enabledRules: [ActionEnabledRule.Always],
    scope: [Scope.AccountWrite]
  };

  static haveAccessToNotes: TableAction = {
    name: 'Notes',
    iconEnabled: RavIconCircled.Note,
    iconDisabled: RavIconCircledOutlined.Note,
    mobileIcon: RavIconOutlined.Note,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly],
    scope: '*'
  };

  static haveAccessToDownload: TableAction = {
    name: 'Download',
    iconEnabled: RavIconCircled.Download,
    iconDisabled: RavIconCircledOutlined.Download,
    mobileIcon: RavIconOutlined.Download,
    enabledRules: [ActionEnabledRule.OnOneOrMoreSelections],
    scope: '*'
  };

  static haveAccessToScreenShare: TableAction = {
    name: 'Screen Share',
    iconEnabled: RavIconCircled.ScreenShare,
    iconDisabled: RavIconCircledOutlined.ScreenShare,
    mobileIcon: RavIconOutlined.ScreenShare,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly],
    scope: '*'
  };

  static rename: TableAction = {
    name: 'Rename',
    iconEnabled: RavIconCircled.Rename,
    iconDisabled: RavIconCircledOutlined.Pencil,
    mobileIcon: RavIconOutlined.PencilLine,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly, ActionEnabledRule.OnNoErrorFilesSelected],
    scope: [Scope.FolderWrite, Scope.DocumentWrite]
  };

  static openInNewTab: TableAction = {
    name: 'Open in new tab',
    iconEnabled: RavIconCircled.NewTab,
    mobileIcon: RavIconOutlined.NewTab,
    enabledRules: [ActionEnabledRule.OnOneSelectionOnly, ActionEnabledRule.OnNoErrorFilesSelected],
    scope: '*'
  };

  /**
   * Determines if the action should be enabled based on it's rules and the amount of selected items.
   */
  static isEnabled(action: TableAction, selectedRows: any[]): boolean {
    return this.evaluateRules(action.enabledRules, selectedRows);
  }

  /**
   * Determines if the action should be shown based on it's rules (if present) and the amount of selected items.
   */
  static shouldShow(action: TableAction, selectedRows: any[]): boolean {
    return action.showingRules ? this.evaluateRules(action.showingRules, selectedRows) : true;
  }

  static evaluateRule(enabledRule: ActionEnabledRule, selectedRows: any[]): boolean {
    let e2eEncryptionStatus;
    switch (enabledRule) {
      case ActionEnabledRule.OnOneSelectionOnly:
        return selectedRows.length === 1;
      case ActionEnabledRule.OnOneOrMoreSelections:
        return selectedRows.length >= 1;
      case ActionEnabledRule.OnTwoOrMoreSelections:
        return selectedRows.length >= 2;
      case ActionEnabledRule.OnDocumentOnlySelections:
        // Share should only work for documents selected
        return selectedRows.length > 0 && selectedRows.every((row) => row.type === 'document');
      case ActionEnabledRule.OnTwoPdfsSelected:
        // Combine action only works for 2 PDFs selected
        return selectedRows.length === 2 && selectedRows.every((row) => row.mime_type === 'application/pdf');
      case ActionEnabledRule.E2EEncryptionShouldBeDisabled:
        if (localStorage.getItem('userWithToken')) {
          e2eEncryptionStatus = JSON.parse(localStorage.getItem('userWithToken')).account.e2e_encryption;
          return e2eEncryptionStatus === AccountE2eEncryption.Inactive;
        }
        break;
      case ActionEnabledRule.If2OrMoreSelected_E2EEShouldBeDisabled:
        if (localStorage.getItem('userWithToken')) {
          e2eEncryptionStatus = JSON.parse(localStorage.getItem('userWithToken')).account.e2e_encryption;
          return selectedRows.length > 1 ? e2eEncryptionStatus === AccountE2eEncryption.Inactive : true;
        }
        break;
      case ActionEnabledRule.OnNoErrorFilesSelected:
        return (
          selectedRows.length > 0 &&
          selectedRows.every((row) => (row.processing_status ? row.processing_status !== 'Error' : true))
        );
      case ActionEnabledRule.Always:
      default:
        return true;
    }
  }

  private static evaluateRules(rules: ActionEnabledRule[], selectedRows: any[]) {
    let result = true;
    rules.map((rule) => {
      result = result && this.evaluateRule(rule, selectedRows);
    });
    return result;
  }
}
export class RowsActions {
  static resetPassword: RowAction = {
    name: 'Reset Password',
    cellValueOverride: 'Reset Password',
    scope: Scope.UserWrite
  };

  static downloadInvoicePdf: RowAction = {
    name: 'Download PDF',
    scope: Scope.AccountWrite
  };
}
